// src/pages/ProjectsListPage.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiSearch, FiInfo } from 'react-icons/fi';
import ProjectCard from '../components/common/ProjectCard/ProjectCard';
import { ProjectsService } from '../services/projects.service';
import { Project } from '../types/Project';
import styles from './ProjectsListPage.module.css';
import { useAuth } from '../context/AuthContext';

interface ProjectsListPageProps {
  publicView?: boolean;
}

const ProjectsListPage: React.FC<ProjectsListPageProps> = ({ publicView = false }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    const scope = !publicView && user?.role === 'developer' ? 'developer' : 'public';

    ProjectsService.getAll({ scope })
      .then(projects => {
        setProjects(projects);
      })
      .catch(() => {
        setProjects([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [publicView, user?.role]);

  if (loading) return <div className={styles.loading}>{t('common.loading') ?? 'Cargando...'}</div>;

  return (
    <div className={styles.container}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.title}>{t('projects.title')}</h1>
          <p className={styles.subtitle}>{t('projects.subtitle')}</p>
        </div>

        {!publicView && user?.role === 'developer' ? (
          <Link to="/developer/proyectos/new" className={styles.createButton}>
            {t('projects.create_project') || 'Agregar proyecto'}
          </Link>
        ) : null}
      </header>

      <section className={styles.panel}>
        <div className={styles.panelToolbar}>
          <div className={styles.searchField} role="search">
            <FiSearch className={styles.searchIcon} />
            <input type="text" placeholder={t('admin.search')} className={styles.searchInput} />
          </div>

          <button className={styles.filterButton} type="button">
            {t('reports.filter')}
          </button>
        </div>

        {projects.length === 0 ? (
          <div className={styles.emptyState} role="status" aria-live="polite">
            <div className={styles.emptyIcon} aria-hidden="true">
              <FiInfo />
            </div>

            <div className={styles.emptyText}>
              <div className={styles.emptyTitle}>{t('projects.no_projects')}</div>

              {!publicView ? (
                <Link to="/mis-inversiones" className={styles.emptyLink}>
                  <FiDollarSign /> {t('common.investments')}
                </Link>
              ) : null}
            </div>
          </div>
        ) : (
          <div className={styles.projectsGrid}>
            {projects.map(project => {
              const developerView = !publicView && user?.role === 'developer';
              const missingDocs = Number(project.missingDocumentsCount || 0) > 0;
              const notPublished = project.projectState !== 'PUBLISHED';
              const detailDisabled = developerView && (missingDocs || notPublished);
              const disabledReason = missingDocs
                ? 'Podrás ver la vista previa cuando envíes todos los documentos solicitados.'
                : 'La vista pública estará disponible cuando el administrador apruebe y publique el proyecto.';

              return (
                <div key={project.id} className={styles.projectLink}>
                  <ProjectCard
                    project={project}
                    detailDisabled={detailDisabled}
                    disabledReason={detailDisabled ? disabledReason : undefined}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default ProjectsListPage;
